import { Button, Card, CardBody } from '@foundation/ui'
import { Component } from 'react'
import styles from './ErrorBoundary.module.css'
import type { ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null })
    window.location.href = '/'
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className={styles.container}>
          <Card className={styles.card}>
            <CardBody>
              <div className={styles.icon}>Warning</div>
              <h1 className={styles.title}>Something went wrong</h1>
              <p className={styles.description}>
                We're sorry, but something unexpected happened. Please try refreshing the page or
                return to the home page.
              </p>
              {import.meta.env.DEV && this.state.error && (
                <div className={styles.errorDetails}>
                  <h4 className={styles.errorTitle}>Error Details (Development Only)</h4>
                  <pre className={styles.errorMessage}>{this.state.error.message}</pre>
                  {this.state.error.stack && (
                    <pre className={styles.errorStack}>{this.state.error.stack}</pre>
                  )}
                </div>
              )}
              <div className={styles.actions}>
                <Button variant="secondary" onClick={() => window.location.reload()}>
                  Refresh Page
                </Button>
                <Button variant="primary" onClick={this.handleReset}>
                  Return to Home
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      )
    }
    return this.props.children
  }
}
